import isPropValid from "@emotion/is-prop-valid";
import styled from "@emotion/styled";
import { Box, IconButton, Typography } from "@mui/material";
import { ReactComponent as EditIcon } from "Components/_Shared/Icons/Assets/edit.svg";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import type { AnalysisClientItemRelationType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { format } from "date-fns";

const StyledWrapper = styled(Box, {
  shouldForwardProp: isPropValid,
})<{ $selected?: boolean }>`
  ${({ theme, $selected }) => `
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: ${theme.spacing(16)};
    width: 100%;
    border: ${$selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.colors.borderLight}`};
    border-radius: ${theme.spacing(Radius.L)};
    padding: ${theme.spacing(Spacing.XL)};
  `}
`;

type Props = {
  readonly clientId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly birthDate: string;
  readonly selected?: boolean;
  readonly clientRelationType: AnalysisClientItemRelationType;
  readonly onEdit?: (clientId: string, clientRelationType: AnalysisClientItemRelationType) => void;
  readonly onSelect?: (clientId: string) => void;
};

export const FamilyMember: React.FC<Props> = ({
  clientId,
  firstName,
  lastName,
  birthDate,
  clientRelationType,
  selected,
  onEdit,
  onSelect,
}) => {
  const fullName = `${firstName} ${lastName}`;

  return (
    <StyledWrapper
      $selected={selected}
      boxShadow={1}
      onClick={() => {
        onEdit?.(clientId, clientRelationType);
        onSelect?.(clientId);
      }}
    >
      <Box display="flex" alignItems="center">
        <UserInitialsAvatar variant="small" firstName={firstName} surname={lastName} />
        <Box display="flex" flexDirection="column" ml={Spacing.L}>
          <Typography variant="caption" fontWeight={600}>
            {fullName}
          </Typography>
          <Typography variant="caption" color="secondary">
            {birthDate ? format(new Date(birthDate), "d. M. yyyy") : "-"}
          </Typography>
        </Box>
      </Box>
      {onEdit && (
        <IconButton>
          <EditIcon width={18} height={18} />
        </IconButton>
      )}
    </StyledWrapper>
  );
};
